// src/components/TaskChat.jsx - ПОЛНОСТЬЮ ИСПРАВЛЕННАЯ ВЕРСИЯ
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  InputGroup
} from 'react-bootstrap';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useSafeTimeout } from '../hooks/useSafeTimeout';
import { CONSTANTS } from '../constants';
import ChatMessage from './chat/ChatMessage';
import api from '../services/api';

const TaskChat = ({ taskId, taskTitle }) => {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [replyTo, setReplyTo] = useState(null);
  const [roomLocked, setRoomLocked] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [messagesOffset, setMessagesOffset] = useState(0);

  const { user } = useAuth();
  const { subscribe, unsubscribe, send, isConnected } = useWebSocket();
  const { setSafeTimeout, clearSafeTimeout } = useSafeTimeout();

  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const messageIdsRef = useRef(new Set());
  const pendingMessagesRef = useRef(new Map());
  const isUserAtBottomRef = useRef(true);
  const isLoadingMoreRef = useRef(false);

  useEffect(() => {
    console.log('🎣 TaskChat mounted for taskId:', taskId);
    return () => {
      console.log('🎣 TaskChat unmounted for taskId:', taskId);
      if (room) {
        send({
          type: 'unsubscribe',
          data: { channels: [`chat:${room.id}`] }
        });
      }
    };
  }, [taskId, room, send]);

  useEffect(() => {
    const findOrCreateTaskRoom = async () => {
      if (!taskId) return;

      setLoading(true);
      setError('');

      try {
        console.log('🔍 Looking for task room for task:', taskId);
        const response = await api.get('/chat/rooms');
        const rooms = response.data;
        const taskRoom = rooms.find(r => r.type === 'task' && r.task_id === taskId);

        if (taskRoom) {
          console.log('✅ Found task room:', taskRoom);
          setRoom(taskRoom);
          setRoomLocked(taskRoom.is_locked || false);

          const msgsResponse = await api.get(`/chat/rooms/${taskRoom.id}/messages?skip=0&limit=50`);
          const msgsData = msgsResponse.data;
          const reversed = msgsData.reverse();
          
          reversed.forEach(msg => messageIdsRef.current.add(msg.id));
          
          setMessages(reversed);

          if (msgsData.length < 50) {
            setHasMoreMessages(false);
          }
          setMessagesOffset(50);
        } else {
          console.log('❌ Task room not found for task:', taskId);
          setError('Чат для этой задачи еще не создан или у вас недостаточно прав. Он появится автоматически после первого сообщения или назначения исполнителя.');
        }
      } catch (err) {
        console.error('Error loading task chat:', err);
        setError('Не удалось загрузить чат задачи');
      } finally {
        setLoading(false);
      }
    };

    findOrCreateTaskRoom();
  }, [taskId]);

  // Подписка на WebSocket канал комнаты
  useEffect(() => {
    if (!room || !isConnected) return;

    const channel = `chat:${room.id}`;
    console.log('📡 Subscribing to channel:', channel);

    send({
      type: 'subscribe',
      data: { channels: [channel] }
    });

    const handleNewMessage = (data) => {
      // ✅ ДОБАВЛЯЕМ ПРОВЕРКУ room_id
      if (data.room_id !== room?.id) {
        console.log(`⏭️ TaskChat: Message for different room (${data.room_id}), ignoring. Current room: ${room?.id}`);
        return;
      }
      
      console.log('📨 TaskChat WebSocket new message:', data);
      
      // 1. Обработка собственного сообщения (замена временного)
      if (data.user_id === user?.id) {
        const pendingEntry = Array.from(pendingMessagesRef.current.entries())
          .find(([_, pendingMsg]) => {
            const timeDiff = Math.abs(new Date(pendingMsg.created_at) - new Date(data.created_at));
            return pendingMsg.content === data.content && timeDiff < 3000;
          });
        
        if (pendingEntry) {
          const [tempId] = pendingEntry;
          console.log('✅ Found pending message, replacing temp ID:', tempId, 'with real ID:', data.id);
          
          setMessages(prev => prev.map(msg => 
            msg.id === tempId ? { 
              ...data, 
              user_name: data.user_name || user?.name 
            } : msg
          ));
          
          pendingMessagesRef.current.delete(tempId);
          messageIdsRef.current.add(data.id);
          return;
        }
      }

      // 2. Проверка дубликатов по ID
      if (messageIdsRef.current.has(data.id)) {
        console.log(`⏭️ Duplicate message ${data.id} ignored`);
        return;
      }

      // 3. Добавляем ID в Set и сообщение в список
      messageIdsRef.current.add(data.id);
      setMessages(prev => {
        if (prev.some(m => m.id === data.id)) {
          console.log('⏭️ Message already exists in state, ignoring');
          return prev;
        }
        console.log('➕ Adding new message to state:', data);
        return [...prev, data];
      });

      // 4. Скролл вниз если нужно
      if (isUserAtBottomRef.current) {
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    };

    const handleTyping = (data) => {
      const { user_id, user_name, is_typing } = data;
      if (user_id === user?.id) return;

      setTypingUsers(prev => {
        if (is_typing) {
          return {
            ...prev,
            [user_id]: { user_name, timestamp: Date.now() }
          };
        } else {
          const newState = { ...prev };
          delete newState[user_id];
          return newState;
        }
      });
    };

    const handleRoomLocked = (data) => {
      if (data.room_id === room.id) {
        console.log('🔒 Room locked:', data.room_id);
        setRoomLocked(true);
      }
    };

    subscribe('chat:new_message', handleNewMessage);
    subscribe('chat:typing', handleTyping);
    subscribe('chat:room_locked', handleRoomLocked);

    return () => {
      console.log('📡 Unsubscribing from channel:', channel);
      unsubscribe('chat:new_message', handleNewMessage);
      unsubscribe('chat:typing', handleTyping);
      unsubscribe('chat:room_locked', handleRoomLocked);
    };
  }, [room, isConnected, user?.id, send, subscribe, unsubscribe]);

  // Проверка нахождения внизу
  const checkIfAtBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 40;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  // Обработчик скролла для подгрузки старых сообщений
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    isUserAtBottomRef.current = checkIfAtBottom();

    if (container.scrollTop < 50 && hasMoreMessages && !isLoadingMoreRef.current && !loadingMessages) {
      isLoadingMoreRef.current = true;
      const oldHeight = container.scrollHeight;

      const loadOlderMessages = async () => {
        try {
          console.log('📚 Loading older messages, offset:', messagesOffset);
          const response = await api.get(`/chat/rooms/${room.id}/messages?skip=${messagesOffset}&limit=50`);
          const olderMessages = response.data;
          const reversed = olderMessages.reverse();

          if (olderMessages.length > 0) {
            const newMessages = reversed.filter(msg => !messageIdsRef.current.has(msg.id));

            if (newMessages.length > 0) {
              newMessages.forEach(msg => messageIdsRef.current.add(msg.id));
              setMessages(prev => [...newMessages, ...prev]);
              setMessagesOffset(prev => prev + olderMessages.length);

              setTimeout(() => {
                if (container) {
                  container.scrollTop = container.scrollHeight - oldHeight;
                }
              }, 10);
            }

            if (olderMessages.length < 50) {
              setHasMoreMessages(false);
            }
          } else {
            setHasMoreMessages(false);
          }
        } catch (err) {
          console.error('Error loading older messages:', err);
        } finally {
          isLoadingMoreRef.current = false;
        }
      };

      loadOlderMessages();
    }
  }, [room, hasMoreMessages, loadingMessages, messagesOffset, checkIfAtBottom]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    if (messages.length > 0 && isUserAtBottomRef.current) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTypingUsers(prev => {
        const newState = { ...prev };
        Object.keys(newState).forEach(userId => {
          if (now - newState[userId].timestamp > 3000) {
            delete newState[userId];
          }
        });
        return newState;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Отправка сообщения
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !room || roomLocked || sending) return;

    setSending(true);

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const tempMessage = {
      id: tempId,
      room_id: room.id,
      user_id: user?.id,
      user_name: user?.name || 'Вы',
      content: messageText,
      is_system: false,
      created_at: new Date().toISOString(),
      status: 'sending',
      reply_to: replyTo ? { 
        id: replyTo.id, 
        user_name: replyTo.user_name, 
        content: replyTo.content 
      } : null
    };

    console.log('📤 Sending message with temp ID:', tempId);

    pendingMessagesRef.current.set(tempId, {
      content: messageText,
      created_at: tempMessage.created_at
    });

    setMessages(prev => [...prev, tempMessage]);
    setMessageText('');
    setReplyTo(null);

    try {
      const response = await api.post(`/chat/rooms/${room.id}/messages`, {
        room_id: room.id,
        content: messageText,
        reply_to_id: replyTo?.id
      });

      const sentMessage = response.data;
      console.log('✅ Message sent successfully, real ID:', sentMessage.id);

      messageIdsRef.current.add(sentMessage.id);
      pendingMessagesRef.current.delete(tempId);
      
      // Заменяем временное сообщение на реальное
      setMessages(prev => prev.map(msg =>
        msg.id === tempId ? { 
          ...sentMessage, 
          user_name: sentMessage.user_name || user?.name 
        } : msg
      ));

    } catch (err) {
      console.error('❌ Error sending message:', err);
      
      pendingMessagesRef.current.delete(tempId);
      setMessages(prev => prev.map(msg =>
        msg.id === tempId ? { ...msg, status: 'failed' } : msg
      ));
    } finally {
      setSending(false);
    }
  };

  const handleTyping = useCallback((isTyping) => {
    if (!room || !isConnected) return;

    if (typingTimeoutRef.current) {
      clearSafeTimeout(typingTimeoutRef.current);
    }

    send({
      type: 'chat:typing',
      data: {
        room_id: room.id,
        is_typing: isTyping
      }
    });

    if (isTyping) {
      typingTimeoutRef.current = setSafeTimeout(() => {
        send({
          type: 'chat:typing',
          data: {
            room_id: room.id,
            is_typing: false
          }
        });
      }, 3000);
    }
  }, [room, isConnected, send, clearSafeTimeout, setSafeTimeout]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessageText(value);
    handleTyping(value.length > 0);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
  };

  const typingText = () => {
    const typingArray = Object.values(typingUsers);
    if (typingArray.length === 0) return '';
    if (typingArray.length === 1) return `${typingArray[0].user_name} печатает...`;
    if (typingArray.length === 2) return `${typingArray[0].user_name} и ${typingArray[1].user_name} печатают...`;
    return 'Несколько человек печатают...';
  };

  const groupedMessages = React.useMemo(() => {
    const groups = [];
    let lastDate = null;

    for (const message of messages) {
      const date = new Date(message.created_at).toDateString();

      if (date !== lastDate) {
        groups.push({
          type: 'date',
          date: message.created_at,
          id: `date-${date}-${message.id}`
        });
        lastDate = date;
      }

      groups.push(message);
    }

    return groups;
  }, [messages]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Загрузка чата...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-chat-square-text display-4 text-muted mb-3 opacity-50"></i>
        <h5 className="text-muted">Чат недоступен</h5>
        <p className="text-muted small">{error}</p>
        {!room && (
          <p className="text-muted small mt-2">
            <i className="bi bi-info-circle me-1"></i>
            Чат появится после назначения исполнителя или первого сообщения
          </p>
        )}
      </div>
    );
  }

  if (!room) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-chat-square-text display-4 text-muted mb-3 opacity-50"></i>
        <h5 className="text-muted">Чат для задачи #{taskId}</h5>
        <p className="text-muted small mb-3">
          Здесь будут отображаться обсуждения задачи
        </p>
        <Badge bg="info" className="p-2">
          <i className="bi bi-clock me-1"></i>
          Ожидайте, чат создается автоматически...
        </Badge>
      </div>
    );
  }

  return (
    <div className="task-chat" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
      <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
        <div>
          <h6 className="mb-0">
            <i className="bi bi-chat-dots me-2 text-primary"></i>
            Обсуждение задачи
          </h6>
          <small className="text-muted">
            {room.participants?.length || 0} участников
            {roomLocked && (
              <Badge bg="danger" className="ms-2">
                <i className="bi bi-lock me-1"></i>
                закрыто
              </Badge>
            )}
          </small>
        </div>
        {!isConnected && (
          <Badge bg="warning" text="dark">
            <i className="bi bi-wifi-off me-1"></i>
            офлайн
          </Badge>
        )}
      </div>

      <div
        ref={messagesContainerRef}
        className="flex-grow-1 overflow-auto p-3"
        style={{ backgroundColor: '#f8f9fa' }}
      >
        {loadingMessages ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : groupedMessages.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-chat display-4 text-muted opacity-50"></i>
            <p className="text-muted mt-2">Нет сообщений</p>
            <small className="text-muted">
              Напишите первое сообщение в обсуждении задачи
            </small>
          </div>
        ) : (
          groupedMessages.map((item) => {
            if (item.type === 'date') {
              return (
                <div key={item.id} className="text-center my-3">
                  <Badge bg="light" text="dark" className="py-1 px-3">
                    {new Date(item.date).toLocaleDateString('ru-RU', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Badge>
                </div>
              );
            }

            const isOwn = item.user_id === user?.id;

            if (item.is_system) {
              return (
                <div key={item.id} className="text-center my-3">
                  <Badge bg="secondary" className="py-1 px-3" style={{ fontSize: '0.8rem' }}>
                    <i className="bi bi-gear me-1"></i>
                    {item.content}
                  </Badge>
                </div>
              );
            }

            return (
              <div
                key={item.id}
                className={`d-flex ${isOwn ? 'justify-content-end' : 'justify-content-start'} mb-3`}
              >
                <div className={`d-flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`} style={{ maxWidth: '70%' }}>
                  <div className="flex-shrink-0">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white"
                      style={{
                        width: '36px',
                        height: '36px',
                        fontSize: '0.9rem',
                        background: isOwn ? '#8dc0fb' : '#6c757d'
                      }}
                    >
                      {getInitials(item.user_name)}
                    </div>
                  </div>

                  <div>
                    {item.reply_to && (
                      <div className="mb-1 p-2 bg-white rounded border-start border-primary" style={{ fontSize: '0.8rem' }}>
                        <small className="text-muted d-block">
                          <i className="bi bi-arrow-return-right me-1"></i>
                          В ответ на {item.reply_to.user_name}:
                        </small>
                        <div className="text-truncate" style={{ maxWidth: '200px' }}>
                          {item.reply_to.content}
                        </div>
                      </div>
                    )}

                    <div
                      className={`rounded-3 p-2 ${
                        item.status === 'failed' ? 'bg-danger bg-opacity-10 border border-danger' :
                        isOwn ? 'bg-primary text-white' : 'bg-white border'
                      }`}
                    >
                      {!isOwn && (
                        <small className="fw-semibold d-block text-muted mb-1">
                          {item.user_name}
                        </small>
                      )}

                      <p className="mb-0 d-flex align-items-center" style={{ whiteSpace: 'pre-wrap' }}>
                        {item.content}
                        {item.status === 'sending' && (
                          <Spinner animation="border" size="sm" className="ms-2" />
                        )}
                        {item.status === 'failed' && (
                          <i className="bi bi-exclamation-triangle text-danger ms-2"></i>
                        )}
                      </p>
                    </div>

                    <div className="d-flex justify-content-end mt-1">
                      <small className="text-muted">
                        {formatTime(item.created_at)}
                        {item.edited_at && <span className="ms-1">(ред.)</span>}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {typingText() && (
          <div className="text-muted small fst-italic mt-2">
            {typingText()}
          </div>
        )}
      </div>

      <div className="p-3 border-top bg-light">
        <Form onSubmit={handleSendMessage}>
          <InputGroup>
            <Form.Control
              as="textarea"
              rows={1}
              placeholder={roomLocked ? "Чат закрыт" : "Напишите сообщение..."}
              value={messageText}
              onChange={handleInputChange}
              disabled={!room || roomLocked || !isConnected}
              style={{ resize: 'none' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
            />
            <Button
              type="submit"
              variant="primary"
              disabled={!messageText.trim() || sending || !room || roomLocked || !isConnected}
            >
              {sending ? <Spinner animation="border" size="sm" /> : <i className="bi bi-send"></i>}
            </Button>
          </InputGroup>
          {!isConnected && (
            <small className="text-danger d-block mt-2">
              <i className="bi bi-wifi-off me-1"></i>
              Нет соединения. Сообщения будут отправлены при восстановлении связи.
            </small>
          )}
        </Form>
      </div>
    </div>
  );
};

export default TaskChat;